import React, { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePmsTheme } from "../Components/PmsThemeContext";
import "../rms/rms.css";
import {
  buildBusinessUnitPath,
  readRmsSessionUser,
  resolveBusinessUnitId,
} from "../rms/rmsApi";
import RmsSetupView from "../rms/RmsSetupView";
import RmsSignalsForecastView from "../rms/RmsSignalsForecastView";
import RmsMarketBenchmarkView from "../rms/RmsMarketBenchmarkView";
import RmsRecommendationsView from "../rms/RmsRecommendationsView";
import {
  canApplyMarketPrices,
  canApplyRmsWorkflow,
  canApproveRmsWorkflow,
  canConfigureRms,
  canRollbackRms,
  canViewRms,
} from "../rms/rmsAccessControl";

const VIEW_DEFINITIONS = [
  {
    key: "setup",
    labelKey: "setup",
    route: "/revenue-management-container/revenue-optimizer/setup",
    requiresAccessSettings: true,
  },
  {
    key: "signals-forecast",
    labelKey: "signalsForecast",
    route: "/revenue-management-container/revenue-optimizer/signals-forecast",
  },
  {
    key: "market-benchmark",
    labelKey: "marketBenchmark",
    route: "/revenue-management-container/revenue-optimizer/market-benchmark",
  },
  {
    key: "recommendations",
    labelKey: "recommendations",
    route: "/revenue-management-container/revenue-optimizer/recommendations",
  },
];

const REVENUE_OPTIMIZER_MESSAGES = {
  en: {
    title: "Revenue Optimizer",
    subtitle:
      "Review hotel setup, demand signals, market comparison and final recommendations in one place.",
    noPermission: "You don't have permission to access this section.",
    views: {
      setup: "Setup",
      signalsForecast: "Signals & Forecast",
      marketBenchmark: "Market Benchmark",
      recommendations: "Recommendations",
    },
  },
  es: {
    title: "Optimizador de ingresos",
    subtitle:
      "Revisa la configuraci\u00f3n del hotel, las se\u00f1ales de demanda, la comparaci\u00f3n de mercado y las recomendaciones finales en un solo lugar.",
    noPermission: "No tienes permiso para acceder a esta secci\u00f3n.",
    views: {
      setup: "Configuraci\u00f3n",
      signalsForecast: "Se\u00f1ales y previsi\u00f3n",
      marketBenchmark: "Benchmark de mercado",
      recommendations: "Recomendaciones",
    },
  },
  ca: {
    title: "Optimitzador d'ingressos",
    subtitle:
      "Revisa la configuraci\u00f3 de l'hotel, els senyals de demanda, la comparativa de mercat i les recomanacions finals en un sol lloc.",
    noPermission: "No tens perm\u00eds per accedir a aquesta secci\u00f3.",
    views: {
      setup: "Configuraci\u00f3",
      signalsForecast: "Senyals i previsi\u00f3",
      marketBenchmark: "Benchmark de mercat",
      recommendations: "Recomanacions",
    },
  },
};

const revenueOptimizerViewCss = `
  .rmo-shell--light {
    --rmo-light-bg: #f4f8fb;
    --rmo-light-surface: #ffffff;
    --rmo-light-surface-strong: #ffffff;
    --rmo-light-surface-soft: #f7fafc;
    --rmo-light-border: #d7e2ec;
    --rmo-light-border-strong: #bdcad8;
    --rmo-light-border-soft: rgba(34, 58, 122, 0.1);
    --rmo-light-title: #172653;
    --rmo-light-text: #26354f;
    --rmo-light-muted: #5b6680;
    --rmo-light-blue: #223a7a;
    --rmo-light-horizon: #3db6e9;
    --rmo-light-horizon-soft: rgba(61, 182, 233, 0.12);
    --rmo-light-connect: #63c1c7;
    --rmo-light-connect-soft: rgba(99, 193, 199, 0.12);
    --rmo-light-danger: #9b3445;
    --rmo-light-danger-soft: rgba(155, 52, 69, 0.1);
    --rmo-light-shadow: 0 18px 42px rgba(34, 58, 122, 0.12);
    color: var(--rmo-light-text);
    color-scheme: light;
  }

  .rmo-shell--light .rmo-workspace {
    border-color: var(--rmo-light-border);
    border-top-color: var(--rmo-light-horizon);
    background: var(--rmo-light-bg);
    box-shadow: var(--rmo-light-shadow);
  }

  .rmo-shell--light .rmo-workspace > .rmo-head,
  .rmo-shell--light .rmo-workspace > .rmo-toolbar {
    background: #ffffff;
  }

  .rmo-shell--light .rmo-workspace > .rmo-contentPad {
    background: var(--rmo-light-bg);
    scrollbar-color: rgba(34, 58, 122, 0.34) #e8eff5;
  }

  .rmo-shell--light .rmo-surface {
    border-color: var(--rmo-light-border);
    background: #ffffff;
    box-shadow: var(--rmo-light-shadow);
  }

  .rmo-shell--light .rmo-surface::before {
    background:
      radial-gradient(960px 320px at 7% 0%, rgba(61, 182, 233, 0.08), transparent 58%),
      radial-gradient(840px 280px at 94% 6%, rgba(99, 193, 199, 0.06), transparent 62%);
    opacity: 0.7;
  }

  .rmo-shell--light .rmo-head,
  .rmo-shell--light .rmo-toolbar,
  .rmo-shell--light .rmo-banner,
  .rmo-shell--light .rmo-modalHead,
  .rmo-shell--light .rmo-modalFoot {
    border-color: var(--rmo-light-border-soft);
  }

  .rmo-shell--light .rmo-headTitle,
  .rmo-shell--light .rmo-marketRoomTitle,
  .rmo-shell--light .rmo-competitorCardTitle,
  .rmo-shell--light .rmo-competitorEditorTitle,
  .rmo-shell--light .rmo-strategySummaryTitle,
  .rmo-shell--light .rmo-strategySectionTitle,
  .rmo-shell--light .rmo-choiceCardTitle,
  .rmo-shell--light .rmo-toggleCardTitle,
  .rmo-shell--light .rmo-mapDetailTitle,
  .rmo-shell--light .rmo-standardCardName {
    color: var(--rmo-light-title);
  }

  .rmo-shell--light .rmo-headSubtitle,
  .rmo-shell--light .rmo-bannerText,
  .rmo-shell--light .rmo-kpiMeta,
  .rmo-shell--light .rmo-muted,
  .rmo-shell--light .rmo-cellSub,
  .rmo-shell--light .rmo-cellMeta,
  .rmo-shell--light .rmo-dayHeaderSub,
  .rmo-shell--light .rmo-marketRoomMeta,
  .rmo-shell--light .rmo-priceMeta,
  .rmo-shell--light .rmo-formHelp,
  .rmo-shell--light .rmo-competitorCardMeta,
  .rmo-shell--light .rmo-competitorCardAddress,
  .rmo-shell--light .rmo-competitorStatLabel,
  .rmo-shell--light .rmo-competitorCardFootText,
  .rmo-shell--light .rmo-competitorEditorText,
  .rmo-shell--light .rmo-strategySummaryText,
  .rmo-shell--light .rmo-strategySectionHelp,
  .rmo-shell--light .rmo-choiceCardText,
  .rmo-shell--light .rmo-filterSummaryText,
  .rmo-shell--light .rmo-filterOptionText,
  .rmo-shell--light .rmo-filterOptionCount,
  .rmo-shell--light .rmo-mapRailText,
  .rmo-shell--light .rmo-mapDetailMeta,
  .rmo-shell--light .rmo-standardCardText,
  .rmo-shell--light .rmo-standardBoxCounts,
  .rmo-shell--light .rmo-mapLaneEmpty,
  .rmo-shell--light .rmo-empty {
    color: var(--rmo-light-muted);
  }

  .rmo-shell--light .rmo-kpiLabel,
  .rmo-shell--light .rmo-helpLabel,
  .rmo-shell--light .rmo-formField label,
  .rmo-shell--light .rmo-marketDayHead,
  .rmo-shell--light .rmo-hotelBandName,
  .rmo-shell--light .rmo-filterSummary,
  .rmo-shell--light .rmo-competitorScopeLabel,
  .rmo-shell--light .rmo-competitorEditorEyebrow,
  .rmo-shell--light .rmo-strategySummaryEyebrow,
  .rmo-shell--light .rmo-mapRailEyebrow,
  .rmo-shell--light .rmo-standardCardCode,
  .rmo-shell--light .rmo-mapDetailField span,
  .rmo-shell--light .rmo-mapDetailField label {
    color: #3f4f6b;
  }

  .rmo-shell--light .rmo-kpiValue,
  .rmo-shell--light .rmo-cellMain,
  .rmo-shell--light .rmo-dayHeaderMain,
  .rmo-shell--light .rmo-priceValue,
  .rmo-shell--light .rmo-competitorScopeValue,
  .rmo-shell--light .rmo-competitorStatValue,
  .rmo-shell--light .rmo-mapRailTitle,
  .rmo-shell--light .rmo-standardColumnControl span,
  .rmo-shell--light .rmo-mapDetailField strong,
  .rmo-shell--light .rmo-standardCardTotal {
    color: var(--rmo-light-title);
  }

  .rmo-shell--light .rmo-navBtn,
  .rmo-shell--light .rmo-btn,
  .rmo-shell--light .rmo-linkBtn,
  .rmo-shell--light .rmo-filterTrigger,
  .rmo-shell--light .rmo-filterReset,
  .rmo-shell--light .rmo-chipClearBtn {
    border-color: var(--rmo-light-border);
    background: #ffffff;
    color: var(--rmo-light-title);
    box-shadow: 0 6px 16px rgba(34, 58, 122, 0.06);
  }

  .rmo-shell--light .rmo-navBtn:hover,
  .rmo-shell--light .rmo-btn:hover,
  .rmo-shell--light .rmo-linkBtn:hover,
  .rmo-shell--light .rmo-filterTrigger:hover,
  .rmo-shell--light .rmo-filterReset:hover,
  .rmo-shell--light .rmo-chipClearBtn:hover {
    background: #edf8fc;
    border-color: var(--rmo-light-horizon);
    box-shadow: 0 10px 22px rgba(34, 58, 122, 0.1);
  }

  .rmo-shell--light .rmo-navBtn.isActive,
  .rmo-shell--light .rmo-btn--accent,
  .rmo-shell--light .rmo-filterTrigger.isActive,
  .rmo-shell--light .rmo-priceTileBtn:hover .rmo-priceTile,
  .rmo-shell--light .rmo-choiceCard.isActive,
  .rmo-shell--light .rmo-toggleCard.isActive,
  .rmo-shell--light .rmo-mapLane.isActive,
  .rmo-shell--light .rmo-mapRail.isActive,
  .rmo-shell--light .rmo-standardCard.isActive,
  .rmo-shell--light .rmo-mapChip.isSelected,
  .rmo-shell--light .rmo-typeToken.isSelected {
    border-color: var(--rmo-light-horizon);
    background: #e7f5fb;
    color: var(--rmo-light-blue);
    box-shadow: inset 3px 0 0 var(--rmo-light-blue), 0 10px 22px rgba(34, 58, 122, 0.1);
  }

  .rmo-shell--light .rmo-btn--danger {
    border-color: rgba(155, 52, 69, 0.26);
    background: #fdeff1;
    color: var(--rmo-light-danger);
  }

  .rmo-shell--light .rmo-input,
  .rmo-shell--light .rmo-select,
  .rmo-shell--light .rmo-textarea,
  .rmo-shell--light .rmo-mapChipInput,
  .rmo-shell--light .rmo-mapDetailInput,
  .rmo-shell--light .rmo-standardCardSelect,
  .rmo-shell--light .rmo-standardColumnSelect {
    border-color: var(--rmo-light-border);
    background: #ffffff;
    color: var(--rmo-light-title);
  }

  .rmo-shell--light .rmo-input::placeholder,
  .rmo-shell--light .rmo-textarea::placeholder,
  .rmo-shell--light .rmo-mapChipInput::placeholder,
  .rmo-shell--light .rmo-mapDetailInput::placeholder {
    color: #7a879c;
  }

  .rmo-shell--light .rmo-input:focus,
  .rmo-shell--light .rmo-select:focus,
  .rmo-shell--light .rmo-textarea:focus,
  .rmo-shell--light .rmo-mapChipInput:focus,
  .rmo-shell--light .rmo-mapDetailInput:focus {
    border-color: var(--rmo-light-horizon);
    box-shadow: 0 0 0 4px rgba(61, 182, 233, 0.14);
    background: #ffffff;
  }

  .rmo-shell--light .rmo-helpDot,
  .rmo-shell--light .rmo-filterTriggerCount,
  .rmo-shell--light .rmo-mapLaneCount {
    border-color: rgba(61, 182, 233, 0.28);
    background: var(--rmo-light-horizon-soft);
    color: var(--rmo-light-blue);
  }

  .rmo-shell--light .rmo-kpiCard,
  .rmo-shell--light .rmo-banner,
  .rmo-shell--light .rmo-competitorScopeCard,
  .rmo-shell--light .rmo-competitorCard,
  .rmo-shell--light .rmo-competitorEditorHero,
  .rmo-shell--light .rmo-strategyEditor,
  .rmo-shell--light .rmo-strategySummary,
  .rmo-shell--light .rmo-strategySection,
  .rmo-shell--light .rmo-choiceCard,
  .rmo-shell--light .rmo-toggleCard,
  .rmo-shell--light .rmo-selectionBanner,
  .rmo-shell--light .rmo-unassignedBoard,
  .rmo-shell--light .rmo-standardBox,
  .rmo-shell--light .rmo-mapRail,
  .rmo-shell--light .rmo-standardColumnHeader,
  .rmo-shell--light .rmo-standardCard,
  .rmo-shell--light .rmo-mapDetail,
  .rmo-shell--light .rmo-dropZone,
  .rmo-shell--light .rmo-modal,
  .rmo-shell--light .rmo-errorBox {
    border-color: var(--rmo-light-border);
    background: #ffffff;
    box-shadow: 0 14px 34px rgba(34, 58, 122, 0.1);
  }

  .rmo-shell--light .rmo-dropZone.isFilled {
    background: #f7fafc;
  }

  .rmo-shell--light .rmo-dropZone.isActive,
  .rmo-shell--light .rmo-mapRail--own.isActive,
  .rmo-shell--light .rmo-mapRail--competitor.isActive {
    box-shadow: 0 0 0 1px rgba(61, 182, 233, 0.32), 0 12px 28px rgba(34, 58, 122, 0.12);
  }

  .rmo-shell--light .rmo-tableWrap,
  .rmo-shell--light .rmo-marketTableWrap,
  .rmo-shell--light .rmo-mapRailList,
  .rmo-shell--light .rmo-standardColumnList {
    background: #f7fafc;
    scrollbar-color: rgba(34, 58, 122, 0.36) #e8eff5;
  }

  .rmo-shell--light .rmo-tableWrap::-webkit-scrollbar,
  .rmo-shell--light .rmo-marketTableWrap::-webkit-scrollbar,
  .rmo-shell--light .rmo-mapRailList::-webkit-scrollbar,
  .rmo-shell--light .rmo-standardColumnList::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  .rmo-shell--light .rmo-tableWrap::-webkit-scrollbar-thumb,
  .rmo-shell--light .rmo-marketTableWrap::-webkit-scrollbar-thumb,
  .rmo-shell--light .rmo-mapRailList::-webkit-scrollbar-thumb,
  .rmo-shell--light .rmo-standardColumnList::-webkit-scrollbar-thumb {
    background: rgba(34, 58, 122, 0.34);
    border-radius: 999px;
  }

  .rmo-shell--light .rmo-tableWrap::-webkit-scrollbar-track,
  .rmo-shell--light .rmo-marketTableWrap::-webkit-scrollbar-track,
  .rmo-shell--light .rmo-mapRailList::-webkit-scrollbar-track,
  .rmo-shell--light .rmo-standardColumnList::-webkit-scrollbar-track {
    background: #e8eff5;
  }

  .rmo-shell--light .rmo-table thead th {
    background: #eaf4f8 !important;
    border-color: var(--rmo-light-border-soft) !important;
    color: var(--rmo-light-title) !important;
    box-shadow: inset 0 -2px 0 var(--rmo-light-horizon);
  }

  .rmo-shell--light .rmo-table tbody tr {
    background: #ffffff;
  }

  .rmo-shell--light .rmo-table tbody tr:nth-child(even) {
    background: #f7fafc;
  }

  .rmo-shell--light .rmo-table td {
    border-color: var(--rmo-light-border-soft);
    color: var(--rmo-light-text);
  }

  .rmo-shell--light .rmo-table .rmo-sticky,
  .rmo-shell--light .rmo-table thead .rmo-sticky {
    background: inherit;
  }

  .rmo-shell--light .rmo-bandRow td,
  .rmo-shell--light .rmo-hotelBandRow td {
    background: linear-gradient(90deg, #e5f4f8, #f4f8fb);
  }

  .rmo-shell--light .rmo-hotelBandBar {
    background: linear-gradient(90deg, rgba(99, 193, 199, 0.28), rgba(61, 182, 233, 0.28));
  }

  .rmo-shell--light .rmo-priceTile,
  .rmo-shell--light .rmo-marketPriceCell {
    border-color: var(--rmo-light-border);
    background: #ffffff;
  }

  .rmo-shell--light .rmo-priceTile.isClosed {
    background: #edf2f6;
    border-color: var(--rmo-light-border-strong);
  }

  .rmo-shell--light .rmo-pill,
  .rmo-shell--light .rmo-chip,
  .rmo-shell--light .rmo-filterPill,
  .rmo-shell--light .rmo-typeToken,
  .rmo-shell--light .rmo-mapChip,
  .rmo-shell--light .rmo-standardGroup {
    border-color: var(--rmo-light-border);
    background: #f7fafc;
    color: var(--rmo-light-title);
  }

  .rmo-shell--light .rmo-pill--success {
    border-color: rgba(99, 193, 199, 0.32);
    background: var(--rmo-light-connect-soft);
    color: #22666a;
  }

  .rmo-shell--light .rmo-pill--warn {
    border-color: rgba(61, 182, 233, 0.32);
    background: var(--rmo-light-horizon-soft);
    color: #2b728e;
  }

  .rmo-shell--light .rmo-pill--danger {
    border-color: rgba(155, 52, 69, 0.24);
    background: var(--rmo-light-danger-soft);
    color: var(--rmo-light-danger);
  }

  .rmo-shell--light .rmo-chip--standard,
  .rmo-shell--light .rmo-standardGroup--own,
  .rmo-shell--light .rmo-mapChip--own,
  .rmo-shell--light .rmo-mapDetailType--own {
    background: var(--rmo-light-horizon-soft);
    color: var(--rmo-light-blue);
  }

  .rmo-shell--light .rmo-chip--category,
  .rmo-shell--light .rmo-standardGroup--competitor,
  .rmo-shell--light .rmo-mapChip--competitor,
  .rmo-shell--light .rmo-mapDetailType--competitor {
    background: var(--rmo-light-connect-soft);
    color: #22666a;
  }

  .rmo-shell--light .rmo-chip--muted,
  .rmo-shell--light .rmo-pill--soft {
    color: var(--rmo-light-muted);
  }

  .rmo-shell--light .rmo-deltaUp {
    color: #22666a;
  }

  .rmo-shell--light .rmo-deltaDown {
    color: var(--rmo-light-danger);
  }

  .rmo-shell--light .rmo-modalBackdrop {
    background: rgba(23, 38, 83, 0.32);
    backdrop-filter: blur(8px);
  }

  .rmo-shell--light .rmo-errorBox {
    color: var(--rmo-light-danger);
    border-color: rgba(155, 52, 69, 0.24);
    background: #fdeff1;
  }

  .rmo-shell--light .rmo-modalHead,
  .rmo-shell--light .rmo-modalFoot {
    background: #ffffff;
  }

  .rmo-shell--light .rmo-modalBody {
    background: #f7fafc;
  }

  .rmo-shell--light .rmo-modal,
  .rmo-shell--light .rmo-selectionBanner {
    color: var(--rmo-light-text);
  }

  .rmo-shell--light .rmo-btn--accent:not(:disabled) {
    border-color: var(--rmo-light-blue);
    background: var(--rmo-light-blue);
    color: #ffffff;
    box-shadow: 0 8px 18px rgba(34, 58, 122, 0.2);
  }

  .rmo-shell--light .rmo-btn--accent:not(:disabled):hover {
    border-color: #172653;
    background: #172653;
    color: #ffffff;
  }

  .rmo-shell--light .rmo-btn:disabled,
  .rmo-shell--light .rmo-navBtn:disabled {
    opacity: 1;
    border-color: #d7e2ec;
    background: #edf2f6;
    color: #5b6680;
    box-shadow: none;
  }

  .rmo-shell--light .rmo-input::placeholder,
  .rmo-shell--light .rmo-textarea::placeholder,
  .rmo-shell--light .rmo-mapChipInput::placeholder,
  .rmo-shell--light .rmo-mapDetailInput::placeholder {
    color: #66738a;
  }

  .rmo-shell--light .rmo-filterOptionRow,
  .rmo-shell--light .rmo-inputSuffix,
  .rmo-shell--light .rmo-mapLane,
  .rmo-shell--light .rmo-mapLaneEmpty,
  .rmo-shell--light .rmo-standardGroupEmpty,
  .rmo-shell--light .rmo-standardCardDefault,
  .rmo-shell--light .rmo-standardCardTotal,
  .rmo-shell--light .rmo-mapRailCounter,
  .rmo-shell--light .rmo-mapChipBadge {
    border-color: var(--rmo-light-border);
    background: #f7fafc;
  }

  .rmo-shell--light .rmo-filterOptionRow:hover,
  .rmo-shell--light .rmo-mapLane:hover {
    border-color: var(--rmo-light-horizon);
    background: #edf8fc;
  }

  .rmo-shell--light .rmo-filterOptionRow,
  .rmo-shell--light .rmo-hotelBandTitle,
  .rmo-shell--light .rmo-typeTokenName,
  .rmo-shell--light .rmo-mapLaneHead,
  .rmo-shell--light .rmo-mapChipTitle,
  .rmo-shell--light .rmo-standardGroupHead,
  .rmo-shell--light .rmo-mapRailCounter,
  .rmo-shell--light .rmo-mapChipBadge,
  .rmo-shell--light .rmo-standardCardTotal {
    color: var(--rmo-light-title);
  }

  .rmo-shell--light .rmo-hotelBandSub,
  .rmo-shell--light .rmo-competitorScopeMeta,
  .rmo-shell--light .rmo-typeTokenMeta,
  .rmo-shell--light .rmo-dropZoneEmpty,
  .rmo-shell--light .rmo-mapLaneEmpty,
  .rmo-shell--light .rmo-mapChipMeta,
  .rmo-shell--light .rmo-standardGroupEmpty,
  .rmo-shell--light .rmo-mapDetailEmpty,
  .rmo-shell--light .rmo-mapDetailText {
    color: var(--rmo-light-muted);
  }

  .rmo-shell--light .rmo-hotelBandName {
    background: #e5f4f8 !important;
  }

  .rmo-shell--light .rmo-inputSuffix,
  .rmo-shell--light .rmo-mapChipForm label,
  .rmo-shell--light .rmo-standardCardSelectLabel span {
    color: #33415c;
  }

  .rmo-shell--light .rmo-typeTokenCode,
  .rmo-shell--light .rmo-standardCardCode {
    color: #2b728e;
  }

  .rmo-shell--light .rmo-standardCardDefault {
    color: #22666a;
    background: #edf9f8;
    border-color: #a8dcda;
  }

  .rmo-shell--light .rmo-priceState,
  .rmo-shell--light .rmo-filterPillClose {
    color: var(--rmo-light-danger);
  }

  .rmo-shell--light .rmo-priceState {
    border: 1px solid rgba(155, 52, 69, 0.22);
    background: #fdeff1;
  }

  .rmo-shell--light .rmo-setupActiveStatus > small {
    opacity: 1;
  }

  .rmo-shell--light .rmo-competitorCard.isMuted {
    opacity: 1;
    border-color: var(--rmo-light-border-strong);
    background: #f7fafc;
  }

  .rmo-shell--light .rmo-mapChip.isSaving {
    opacity: 1;
    border-style: dashed;
    border-color: var(--rmo-light-horizon);
    background: #edf8fc;
  }

  .rmo-shell--light .rmo-competitorStat,
  .rmo-shell--light .rmo-priceTile--empty {
    opacity: 1;
    border-color: var(--rmo-light-border);
    background: #f7fafc;
  }
`;

export default function RevenueOptimizerView({ forcedView = "recommendations" }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { themeId, languageId } = usePmsTheme();
  const isLightTheme = themeId === "light";
  const optimizerCopy =
    REVENUE_OPTIMIZER_MESSAGES[languageId] || REVENUE_OPTIMIZER_MESSAGES.en;
  const businessUnitId = resolveBusinessUnitId(searchParams);
  const sessionUser = readRmsSessionUser();
  const permissions = sessionUser?.permissions || {};
  const isAdminRole = Number(sessionUser?.id) === 1;
  const canAccessRevenueOptimizer =
    isAdminRole || canViewRms(permissions);
  const canAccessSettings = isAdminRole || canConfigureRms(permissions);
  const mayApplyMarketPrices =
    isAdminRole || canApplyMarketPrices(permissions);
  const canApproveRecommendations =
    isAdminRole || canApproveRmsWorkflow(permissions);
  const canApplyRecommendations =
    isAdminRole || canApplyRmsWorkflow(permissions);
  const canRollbackRecommendations =
    isAdminRole || canRollbackRms(permissions);

  const localizedViews = useMemo(
    () =>
      VIEW_DEFINITIONS.map((view) => ({
        ...view,
        label:
          optimizerCopy.views[view.labelKey] ||
          REVENUE_OPTIMIZER_MESSAGES.en.views[view.labelKey],
      })),
    [optimizerCopy]
  );

  const availableViews = useMemo(
    () =>
      !canAccessRevenueOptimizer
        ? []
        : localizedViews.filter(
            (view) => !view.requiresAccessSettings || canAccessSettings
          ),
    [canAccessRevenueOptimizer, canAccessSettings, localizedViews]
  );

  const activeView = useMemo(() => {
    const directMatch = availableViews.find((view) => view.key === forcedView);
    return directMatch || availableViews[0] || localizedViews[localizedViews.length - 1];
  }, [availableViews, forcedView, localizedViews]);

  useEffect(() => {
    if (!activeView) return;
    if (activeView.key === forcedView) return;
    navigate(buildBusinessUnitPath(activeView.route, businessUnitId), { replace: true });
  }, [activeView, businessUnitId, forcedView, navigate]);

  const goToView = (view) => {
    navigate(buildBusinessUnitPath(view.route, businessUnitId));
  };

  const shellClassName = `rmo-shell${isLightTheme ? " rmo-shell--light" : ""}`;

  if (!canAccessRevenueOptimizer) {
    return (
      <div className={shellClassName}>
        <style>{revenueOptimizerViewCss}</style>
        <div className="rmo-workspace">
          <div className="rmo-head">
            <div>
              <h2 className="rmo-headTitle">{optimizerCopy.title}</h2>
              <div className="rmo-headSubtitle">{optimizerCopy.noPermission}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={shellClassName}>
      <style>{revenueOptimizerViewCss}</style>
      <div className="rmo-workspace">
        <div className="rmo-head">
          <div>
            <h2 className="rmo-headTitle">{optimizerCopy.title}</h2>
            <div className="rmo-headSubtitle">{optimizerCopy.subtitle}</div>
          </div>
        </div>

        <div className="rmo-toolbar">
          <div className="rmo-nav">
            {availableViews.map((view) => (
              <button
                key={view.key}
                type="button"
                className={`rmo-navBtn${activeView?.key === view.key ? " isActive" : ""}`}
                onClick={() => goToView(view)}
              >
                {view.label}
              </button>
            ))}
          </div>
        </div>

        <div className="rmo-contentPad">
          {activeView?.key === "setup" && (
            <RmsSetupView
              businessUnitId={businessUnitId}
              canAccessSettings={canAccessSettings}
              onContinue={() => {
                const nextView = availableViews.find(
                  (view) => view.key === "signals-forecast"
                );
                if (nextView) goToView(nextView);
              }}
            />
          )}
          {activeView?.key === "signals-forecast" && (
            <RmsSignalsForecastView businessUnitId={businessUnitId} />
          )}
          {activeView?.key === "market-benchmark" && (
            <RmsMarketBenchmarkView
              businessUnitId={businessUnitId}
              canApplyMarketPrices={mayApplyMarketPrices}
            />
          )}
          {activeView?.key === "recommendations" && (
            <RmsRecommendationsView
              businessUnitId={businessUnitId}
              canApproveRecommendations={canApproveRecommendations}
              canApplyRecommendations={canApplyRecommendations}
              canRollbackRecommendations={canRollbackRecommendations}
            />
          )}
        </div>
      </div>
    </div>
  );
}
